import { LOCK_TYPE } from '../types';

import type { VisualizationAxis, VisualizationMeasurement } from './types';

export const JOINT_ANGLE_MEASUREMENTS = {
    left_knee_angle: { label: 'Lead knee', type: 'measurement', axes: ['left_knee_angle_hinge'] },
    right_knee_angle: { label: 'Trail knee', type: 'measurement', axes: ['right_knee_angle_hinge'] },
    left_elbow_angle: { label: 'Lead elbow', type: 'measurement', axes: ['left_elbow_angle_hinge'] },
    right_elbow_angle: { label: 'Trail elbow', type: 'measurement', axes: ['right_elbow_angle_hinge'] },
    left_ankle_angle: {
        label: 'Lead ankle',
        type: 'measurement',
        axes: ['left_ankle_angle_hinge'],
    },
    right_ankle_angle: {
        label: 'Trail ankle',
        type: 'measurement',
        axes: ['right_ankle_angle_hinge'],
    },
} as const satisfies Record<string, VisualizationMeasurement>;

export const JOINT_ANGLE_MEASUREMENTS_PARENT_MAP = Object.fromEntries(
    Object.entries(JOINT_ANGLE_MEASUREMENTS).flatMap(([measurementId, measurement]) =>
        measurement.axes.map((axisId) => [axisId, measurementId] as [string, string]),
    ),
);

export const JOINT_ANGLE_AXES: Record<string, VisualizationAxis> = {
    left_knee_angle_hinge: {
        id: 'left_knee_angle_hinge',
        measurementName: 'left_knee_angle',
        axisIndex: 0,
        label: 'Hinge Joint',
        unit: 'rad',
        targetPoints: ['left_knee', 'left_hip', 'left_ankle'],
        camera: { type: LOCK_TYPE.PLANE },
    },
    right_knee_angle_hinge: {
        id: 'right_knee_angle_hinge',
        measurementName: 'right_knee_angle',
        axisIndex: 0,
        label: 'Hinge Joint',
        unit: 'rad',
        targetPoints: ['right_knee', 'right_hip', 'right_ankle'],
        camera: { type: LOCK_TYPE.PLANE },
    },
    left_elbow_angle_hinge: {
        id: 'left_elbow_angle_hinge',
        measurementName: 'left_elbow_angle',
        axisIndex: 0,
        label: 'Hinge Joint',
        unit: 'rad',
        targetPoints: ['left_elbow', 'left_shoulder', 'left_wrist'],
        camera: { type: LOCK_TYPE.PLANE },
    },
    right_elbow_angle_hinge: {
        id: 'right_elbow_angle_hinge',
        measurementName: 'right_elbow_angle',
        axisIndex: 0,
        label: 'Hinge Joint',
        unit: 'rad',
        targetPoints: ['right_elbow', 'right_shoulder', 'right_wrist'],
        camera: { type: LOCK_TYPE.PLANE },
    },
    left_hip_angle_flexion: {
        id: 'left_hip_angle_flexion',
        measurementName: 'left_hip_angle',
        axisIndex: 0,
        label: 'Hip Flexion',
        unit: 'rad',
        targetPoints: ['pelvis', 'left_hip', 'left_knee'],
        camera: { type: LOCK_TYPE.PLANE },
    },
    right_hip_angle_flexion: {
        id: 'right_hip_angle_flexion',
        measurementName: 'right_hip_angle',
        axisIndex: 0,
        label: 'Hip Flexion',
        unit: 'rad',
        targetPoints: ['pelvis', 'right_hip', 'right_knee'],
        camera: { type: LOCK_TYPE.PLANE },
    },
    left_ankle_angle_flexion: {
        id: 'left_ankle_angle_flexion',
        measurementName: 'left_ankle_angle',
        axisIndex: 0,
        label: 'Ankle Flexion',
        unit: 'rad',
        targetPoints: ['left_knee', 'left_ankle', 'left_foot'],
        camera: { type: LOCK_TYPE.PLANE },
    },
    right_ankle_angle_flexion: {
        id: 'right_ankle_angle_flexion',
        measurementName: 'right_ankle_angle',
        axisIndex: 0,
        label: 'Ankle Flexion',
        unit: 'rad',
        targetPoints: ['right_knee', 'right_ankle', 'right_foot'],
        camera: { type: LOCK_TYPE.PLANE },
    },
    left_ankle_angle_hinge: {
        id: 'left_ankle_angle_hinge',
        measurementName: 'left_ankle_angle',
        axisIndex: 0,
        label: 'Hinge Joint',
        unit: 'rad',

        // TODO: The source measurement defines the foot using heel, big toe and small toe.
        // The existing 3-point joint visualization needs one representative foot point,
        // so big toe is used here.
        targetPoints: ['left_knee', 'left_ankle', 'left_big_toe'],

        // Copy the existing joint-angle camera behavior.
        camera: { type: LOCK_TYPE.PLANE },
    },

    right_ankle_angle_hinge: {
        id: 'right_ankle_angle_hinge',
        measurementName: 'right_ankle_angle',
        axisIndex: 0,
        label: 'Hinge Joint',
        unit: 'rad',

        // TODO: Same approximation as the lead ankle.
        targetPoints: ['right_knee', 'right_ankle', 'right_big_toe'],

        // Copy the existing joint-angle camera behavior.
        camera: { type: LOCK_TYPE.PLANE },
    },
};
